import { Injectable } from '@angular/core';
import {
    HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse,
} from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PayloadCryptoService, PayloadCryptoSession } from './payload-crypto.service';

/** Never encrypted: the key endpoint itself, and client logs (also sent via sendBeacon). */
const EXEMPT_PATHS = ['/crypto/public-key', '/client-logs'];

/**
 * Encrypts JSON request bodies and decrypts JSON responses (including error
 * bodies) for calls to our own API. Registered LAST so it sits closest to the
 * network: AuthInterceptor and LoggingInterceptor see plaintext both ways.
 *
 * - FormData / Blob / binary bodies are sent as-is; only the response is sealed.
 * - Blob/text downloads send X-Enc-Resp: none, so the API leaves them alone.
 * - If the API rejects the session key (rotation, another instance's dev key)
 *   the request is retried once with a fresh key, then sent plain and
 *   encryption pauses for a few minutes (the API decides whether plain is OK).
 * - A response that fails to decrypt (stale HTTP-cache copy sealed for another
 *   tab) is refetched once with Cache-Control: no-cache.
 */
@Injectable()
export class PayloadCryptoInterceptor implements HttpInterceptor {
    constructor(private payloadCrypto: PayloadCryptoService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.payloadCrypto.enabled || !this.applies(req)) {
            return next.handle(req);
        }
        return from(this.payloadCrypto.getSession()).pipe(
            switchMap(session => session ? this.sendEncrypted(req, next, session, true) : next.handle(req))
        );
    }

    private applies(req: HttpRequest<any>): boolean {
        return req.url.startsWith(environment.apiUrl) && !EXEMPT_PATHS.some(p => req.url.includes(p));
    }

    private sendEncrypted(
        req: HttpRequest<any>, next: HttpHandler, session: PayloadCryptoSession, canRetry: boolean
    ): Observable<HttpEvent<any>> {
        // Must match the API's "<METHOD> <path>" (req.originalUrl without query).
        const path = new URL(req.url, window.location.href).pathname;
        const aad = `${req.method.toUpperCase()} ${path}`;

        return from(this.prepare(req, session, `${aad}|req`)).pipe(
            switchMap(encReq => next.handle(encReq)),
            concatMap(event => {
                if (event instanceof HttpResponse && PayloadCryptoService.isEnvelope(event.body)) {
                    return from(this.payloadCrypto.decrypt(session, event.body, `${aad}|res`)).pipe(
                        map(body => event.clone({ body })),
                        // A response sealed with another key: typically a duplicated
                        // tab handed the original tab's response from the HTTP cache.
                        // Refetch once, telling the browser to bypass its cache.
                        catchError(decryptErr => canRetry
                            ? this.sendEncrypted(
                                req.clone({ setHeaders: { 'Cache-Control': 'no-cache' } }), next, session, false)
                            : throwError(() => decryptErr))
                    );
                }
                return of(event);
            }),
            catchError(err => {
                if (!(err instanceof HttpErrorResponse)) {
                    return throwError(() => err);
                }
                return from(readErrorBody(err)).pipe(switchMap(body => {
                    if (err.status === 400 && body?.code === 'ENC_KEY_REJECTED') {
                        if (canRetry) {
                            this.payloadCrypto.reset();
                            return from(this.payloadCrypto.getSession()).pipe(
                                switchMap(s => s ? this.sendEncrypted(req, next, s, false) : next.handle(req))
                            );
                        }
                        this.payloadCrypto.markUnavailable();
                        return next.handle(req);
                    }
                    if (PayloadCryptoService.isEnvelope(err.error)) {
                        return from(this.payloadCrypto.decrypt(session, err.error, `${aad}|res`)).pipe(
                            switchMap(error => throwError(() => new HttpErrorResponse({
                                error,
                                headers: err.headers,
                                status: err.status,
                                statusText: err.statusText,
                                url: err.url ?? undefined,
                            })))
                        );
                    }
                    return throwError(() => err);
                }));
            })
        );
    }

    private async prepare(req: HttpRequest<any>, session: PayloadCryptoSession, aad: string): Promise<HttpRequest<any>> {
        let headers = req.headers.set('X-Enc-Key', session.wrappedKey);
        if (this.payloadCrypto.gzipSupported) {
            headers = headers.set('X-Enc-Gzip', '1');
        }
        if (req.responseType !== 'json') {
            headers = headers.set('X-Enc-Resp', 'none');
        }

        if (isJsonBody(req)) {
            const body = await this.payloadCrypto.encrypt(session, req.body, aad);
            return req.clone({ headers, body });
        }
        return req.clone({ headers });
    }
}

/** True for bodies Angular would serialise as application/json. */
function isJsonBody(req: HttpRequest<any>): boolean {
    const body = req.body;
    if (body === null || body === undefined || typeof body !== 'object') {
        return false;
    }
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer
        || ArrayBuffer.isView(body) || body instanceof URLSearchParams || body instanceof HttpParams) {
        return false;
    }
    const contentType = req.headers.get('Content-Type');
    return !contentType || contentType.includes('application/json');
}

/** Error bodies arrive as Blob/string for non-JSON response types. */
async function readErrorBody(err: HttpErrorResponse): Promise<any> {
    try {
        if (err.error instanceof Blob) {
            return JSON.parse(await err.error.text());
        }
        if (typeof err.error === 'string') {
            return JSON.parse(err.error);
        }
    } catch {
        return null;
    }
    return err.error;
}
